import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNotificationContext } from "contexts/NotificationContext";
import {
  ICancellationFee,
  IHeatMap,
  ILoadSetting,
  ISearchRange,
  ISetting,
  ISpecialHour,
  IUpdateSetting,
} from "models/setting";
import {
  fetchSettingApi,
  updateSettingApi,
} from "api/setting";
import updateSettingValidator from "validators/updateSetting.validator";
import dayjs from "dayjs";

export default function useSetting() {
  const { showError, showSuccess } = useNotificationContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<ILoadSetting>({ loading: true, data: null });
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IUpdateSetting>({
    resolver: yupResolver(updateSettingValidator),
  });

  const watchSpecialHour = watch("special_hour", []);

  useEffect(() => {
    fetchSetting();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (Object.keys(errors).length === 1 && errors.special_hour) {
      // make the focus to id special-hour
      const specialHour: any = document
        .getElementById("special-hour")
        ?.scrollIntoView({ behavior: "smooth" });
      if (specialHour) specialHour.focus();
    }
  }, [errors]);
  const setForm = (value: ISetting) => {
    setValue("search_range", value.search_range?.value as ISearchRange);
    setValue("call_center", value.call_center?.value as string);
    setValue(
      "whatsapp_support_center",
      value.whatsapp_support_center?.value as string
    );
    setValue(
      "telegram_support_center",
      value.telegram_support_center?.value as string
    );
    setValue(
      "email_support_center",
      value.email_support_center?.value as string
    );
    setValue("waiting_time", value.waiting_time?.value as string);
    setValue("tax_percent", value.tax_percent?.value as string);
    setValue("cut_percent", value.cut_percent?.value as string);
    setValue("map_api_key", value.map_api_key?.value as string);
    setValue("app_store_url", value.app_store_url?.value as string);
    setValue("playstore_url", value.playstore_url?.value as string);
    setValue("welcome_mile", value.welcome_mile?.value as string);
    setValue("trip_mile", value.trip_mile?.value as string);
    setValue("driver_min_wallet", value.driver_min_wallet?.value as string);
    setValue("driver_max_debt", String(value.driver_max_debt?.value ?? ""));
    const heatMapValue = value.heat_map_color?.value as IHeatMap | undefined;
    setValue(
      "low_intense_color",
      heatMapValue?.low || ""
    );
    setValue(
      "normal_intense_color",
      heatMapValue?.normal || ""
    );
    setValue(
      "medium_intense_color",
      heatMapValue?.medium || ""
    );
    setValue(
      "high_intense_color",
      heatMapValue?.high || ""
    );
    setValue(
      "cancellation_percent",
      value.cancellation_percent?.value as string
    );
    const specialHourValue = value.special_hour?.value as ISpecialHour[] | undefined;
    const specialHour: any[] = (Array.isArray(specialHourValue) ? specialHourValue : []).map((value) => {
      const startAt = dayjs(`2000-00-00 ${value.start_at}`);
      const endAt = dayjs(`2000-00-00 ${value.end_at}`);
      return {
        ...value,
        start_at: startAt,
        end_at: endAt,
      };
    });
    setValue("special_hour", specialHour);
    setValue(
      "cancellation_fee",
      value.cancellation_fee?.value as ICancellationFee
    );
    setValue("high_demand_percent", value.high_demand_percent?.value as string);
    setValue("decline_penalty_count", String(value.decline_penalty_count?.value ?? ""));
    setValue("decline_penalty_amount", String(value.decline_penalty_amount?.value ?? ""));
    setValue("penalty_expiry_hours", String(value.penalty_expiry_hours?.value ?? ""));
    setValue("lottery_rollout_days", (value.lottery_rollout_days?.value as unknown as string[]) || []);
    setValue("lottery_winners_per_rollout", String(value.lottery_winners_per_rollout?.value ?? ""));
    setValue("lottery_enabled", !!value.lottery_enabled?.value);
    setValue("min_daily_trips", String(value.min_daily_trips?.value ?? ""));
    setValue("idle_penalty_amount", String(value.idle_penalty_amount?.value ?? ""));
    setValue("lottery_prize", String(value.lottery_prize?.value || ""));
    setValue("lottery_split_enabled", !!value.lottery_split_enabled?.value);
    setValue("auto_ban_on_penalty_enabled", !!value.auto_ban_on_penalty_enabled?.value);
    setValue("max_penalty_strikes_for_ban", String(value.max_penalty_strikes_for_ban?.value ?? ""));
    setValue("auto_unban_duration_hours", String(value.auto_unban_duration_hours?.value ?? ""));
  };


  const fetchSetting = async () => {
    const { data, error } = await fetchSettingApi();
    if (data) {
      const temp: ISetting = {} as any;
      for (const value of data.data) {
        temp[value.name] = value;
      }
      setForm(temp);
      setData({ loading: false, data: temp });

      // Extract dispatch config from the response if available
      const dispatchTimeout = temp.dispatch_timeout_minutes?.value as number | undefined;
      const dispatchMaxExpansion = temp.dispatch_max_expansion_multiplier?.value as number | undefined;
      const dispatchCycleCount = temp.dispatch_cycle_count?.value as number | undefined;

      setValue("dispatch_config", {
        max_expansion_multiplier: String(dispatchMaxExpansion ?? 3.0),
        timeout_minutes: String(dispatchTimeout ?? 5.0),
        cycle_count: String(dispatchCycleCount ?? 2),
      });
    } else {
      showError(error!.message);
    }
  };


  const onSubmit = async (input: IUpdateSetting) => {
    setLoading(true);

    // Extract dispatch configuration values
    const dispatchTimeoutMinutes = parseFloat(input.dispatch_config.timeout_minutes);
    const dispatchMaxExpansion = parseFloat(input.dispatch_config.max_expansion_multiplier);
    const dispatchCycleCount = parseInt(input.dispatch_config.cycle_count, 10);

    // Update all settings including dispatch config via main /configurations endpoint
    const settingsPayload: any = {
      app_store_url: { value: input.app_store_url },
      call_center: { value: input.call_center },
      email_support_center: { value: input.email_support_center },
      whatsapp_support_center: { value: input.whatsapp_support_center },
      telegram_support_center: { value: input.telegram_support_center },
      cut_percent: { value: Number(input.cut_percent) },
      driver_min_wallet: { value: Number(input.driver_min_wallet) },
      driver_max_debt: { value: Number(input.driver_max_debt) },
      heat_map_color: {
        value: {
          low: input.low_intense_color,
          normal: input.normal_intense_color,
          medium: input.medium_intense_color,
          high: input.high_intense_color,
        },
      },
      map_api_key: { value: input.map_api_key },
      playstore_url: { value: input.playstore_url },
      search_range: {
        value: {
          latitude: Number(input.search_range.latitude),
          longitude: Number(input.search_range.longitude),
          radius: Number(input.search_range.radius),
        },
      },
      tax_percent: { value: Number(input.tax_percent) },
      trip_mile: { value: Number(input.trip_mile) },
      waiting_time: { value: Number(input.waiting_time) },
      welcome_mile: { value: Number(input.welcome_mile) },
      cancellation_fee: {
        value: {
          amount: Number(input.cancellation_fee.amount),
          count: Number(input.cancellation_fee.count),
        },
      },
      cancellation_percent: { value: Number(input.cancellation_percent) },
      high_demand_percent: { value: Number(input.high_demand_percent) },
      special_hour: {
        value: input.special_hour.map((value) => ({
          end_at: dayjs(value.end_at).format("HH:mm:ss"),
          start_at: dayjs(value.start_at).format("HH:mm:ss"),
          name: value.name,
          percent: Number(value.percent),
        })),
      },
      decline_penalty_count: { value: Number(input.decline_penalty_count) },
      decline_penalty_amount: { value: Number(input.decline_penalty_amount) },
      penalty_expiry_hours: { value: Number(input.penalty_expiry_hours) },
      lottery_rollout_days: { value: input.lottery_rollout_days },
      lottery_winners_per_rollout: { value: Number(input.lottery_winners_per_rollout) },
      lottery_enabled: { value: input.lottery_enabled },
      min_daily_trips: { value: Number(input.min_daily_trips) },
      idle_penalty_amount: { value: Number(input.idle_penalty_amount) },
      lottery_prize: { value: input.lottery_prize },
      lottery_split_enabled: { value: input.lottery_split_enabled },
      auto_ban_on_penalty_enabled: { value: input.auto_ban_on_penalty_enabled },
      max_penalty_strikes_for_ban: { value: Number(input.max_penalty_strikes_for_ban) },
      auto_unban_duration_hours: { value: Number(input.auto_unban_duration_hours) },
    };


    // Always include dispatch config in main settings update via /configurations endpoint
    settingsPayload.dispatch_timeout_minutes = { value: dispatchTimeoutMinutes };
    settingsPayload.dispatch_max_expansion_multiplier = { value: dispatchMaxExpansion };
    settingsPayload.dispatch_cycle_count = { value: dispatchCycleCount };

    const { data, error } = await updateSettingApi(settingsPayload);

    if (data) {
      const temp: ISetting = {} as any;
      for (const value of data.data) {
        temp[value.name] = value;
      }

      // Update the data state and sync the form with API response
      setForm(temp);
      setData({ loading: false, data: temp });

      // Only update dispatch config from response to keep it in sync
      const responseDispatchTimeout = temp.dispatch_timeout_minutes?.value as number | undefined;
      const responseDispatchMaxExpansion = temp.dispatch_max_expansion_multiplier?.value as number | undefined;
      const responseDispatchCycleCount = temp.dispatch_cycle_count?.value as number | undefined;

      if (responseDispatchTimeout !== undefined || responseDispatchMaxExpansion !== undefined || responseDispatchCycleCount !== undefined) {
        setValue("dispatch_config", {
          max_expansion_multiplier: String(responseDispatchMaxExpansion ?? dispatchMaxExpansion),
          timeout_minutes: String(responseDispatchTimeout ?? dispatchTimeoutMinutes),
          cycle_count: String(responseDispatchCycleCount ?? dispatchCycleCount),
        });
      }

      showSuccess("Setting updated Successfully");
    } else {
      showError(error!.message);
    }
    setLoading(false);
  };

  const handleAddSpecialHour = () => {
    const values = watchSpecialHour;
    values.push({
      name: "",
      end_at: "",
      percent: "",
      start_at: "",
    });
    setValue("special_hour", values);
  };

  const handleRemoveSpecialHour = (index: number) => {
    const values = watchSpecialHour;
    values.splice(index, 1);
    setValue("special_hour", values);
  };

  return {
    handleSubmit,
    onSubmit,
    handleAddSpecialHour,
    handleRemoveSpecialHour,
    control,
    loading,
    data,
    errors,
    watchSpecialHour,
  };
}
